import React, { useCallback, useEffect } from "react";
import { useState } from "react";
import { toast } from "react-toastify";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { ReactComponent as EmailIcon } from "assets/icons/email-closed.svg";
import EmailFolderEmptyState from "assets/images/EmailFolderEmptyState.png";
import { DealRoleGuard } from "components/guards";
import { EmailView } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useDealParamsHooks, useFormatDateHook, useUrlHooks } from "hooks";
import { useDealEmailSelector, useDealTitleSelector, useEmailsSelect } from "store";
import { IEmail } from "store/types";
import { Userpilot } from "userpilot";
import { copyToClipboard } from "utils";

import { RoleName } from "../../constants";

export const EmailBox: React.FC = (): JSX.Element => {
  const dealEmail = useDealEmailSelector();
  const dealName = useDealTitleSelector();
  const { sectionId: folderId } = useDealParamsHooks();
  const emails = useEmailsSelect({ section_id: Number(folderId) });
  const formatDate = useFormatDateHook();

  const dealInfo: string = dealName + ` ${"<" + dealEmail + ">"}`;

  const [email, setEmail] = useState<IEmail | null | undefined>(null);
  const { queryParams } = useUrlHooks();
  const { emailBox } = queryParams;

  useEffect(() => {
    if (emailBox && emails.length) {
      setEmail(emails.find((email: IEmail) => email.id === Number(emailBox)));
    }
  }, [emailBox, emails.length]);

  const onClickCopy = useCallback(() => {
    try {
      copyToClipboard(dealInfo);
      toast("Email address was copied to clipboard");
    } catch (_error) {
      console.log("Something went wrong, impossible to copy deal email");
    }
  }, [dealInfo]);

  return (
    <Stack
      direction="column"
      sx={{
        borderRadius: "0 8px 8px 0",
        padding: "20px",
        flexGrow: 1,
        overflow: "hidden",
        width: "100%",
        height: "100%"
      }}
      gap="20px"
    >
      <Stack
        direction="column"
        alignItems="center"
        sx={{
          borderRadius: "6px 6px 0px 0px",
          fontSize: "18px"
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Stack />
          <Button
            sx={{ padding: "4px 8px", height: "24px", minHeight: "24px" }}
            variant="outlined"
            startIcon={<EmailOutlinedIcon sx={{ fontSize: "12px" }} />}
            onClick={onClickCopy}
          >
            <MaterialTooltip title="Copy Deal Email Address" isShow>
              <Typography sx={{ fontSize: "12px" }}>Copy Deal Email Address</Typography>
            </MaterialTooltip>
          </Button>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Stack direction="row" alignItems="center" gap="10px">
            <EmailOutlinedIcon sx={{ fill: "#0A3142" }} />
            <Typography sx={{ fontSize: "16px", fontWeight: "700", color: "#0A3142" }}>Email Box</Typography>
          </Stack>
          <Stack />
        </Stack>
      </Stack>
      <Box
        flexGrow={1}
        sx={{ overflow: "hidden", zIndex: "1", minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        {emails?.length ? (
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: "8px",
              boxShadow: "none",
              height: "100%",
              border: "1px solid #dae0e2",
              zIndex: 1,
              overflow: "auto",
              flexGrow: 1
            }}
          >
            <Table stickyHeader sx={{ zIndex: 1, tableLayout: "fixed", width: "100%" }}>
              <TableHead sx={{ height: "40px" }}>
                <TableRow>
                  <TableCell sx={{ ...tableHeadCellSx }}>Subject</TableCell>
                  <TableCell sx={{ ...tableHeadCellSx }}>Sender Name</TableCell>
                  <TableCell sx={{ ...tableHeadCellSx }}>Date</TableCell>
                  <TableCell sx={{ ...tableHeadCellSx }}>Attachments</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emails?.map((email: IEmail) => (
                  <TableRow key={email.id} sx={{ height: "60px" }}>
                    <TableCell
                      sx={{
                        padding: "7px 0 7px 15px",
                        fontSize: "15px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {/* check for empty file_url */}
                      <MaterialTooltip isShow title={email.email_subject ? email.email_subject : "No subject"}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                          }}
                        >
                          <EmailIcon style={{ minWidth: "20px", width: "20px", height: "16px" }} />
                          <span
                            onClick={() => {
                              setEmail(email);
                              Userpilot.track("Email Opened");
                            }}
                            style={{
                              cursor: "pointer",
                              fontSize: "15px",
                              color: "#26a6ae",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis"
                            }}
                          >
                            {email.email_subject ? email.email_subject : "No subject"}
                          </span>
                        </Box>
                      </MaterialTooltip>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "7px 0 7px 15px",
                        fontSize: "15px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis"
                      }}
                    >
                      <MaterialTooltip isShow title={email.sender_name ? email.sender_name : email.sender_email}>
                        <span
                          style={{
                            fontSize: "15px",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {email.sender_name ? email.sender_name : email.sender_email}
                        </span>
                      </MaterialTooltip>
                    </TableCell>

                    <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                      <TableCell>
                        <span>{formatDate({ date: email.created_at, format: "HH:mm, MMM DD, YYYY" })}</span>
                      </TableCell>
                    </DealRoleGuard>
                    <TableCell>
                      <span>{email.attachments.length || ""}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Stack gap="20px" alignItems="center" sx={{ padding: "40px 0" }}>
            <img src={EmailFolderEmptyState} alt="No emails" loading="lazy" width="180px" />
            <Typography sx={{ color: "#071F35", fontSize: "18px", fontWeight: "500" }}>
              There are no emails in this deal yet.
            </Typography>
            <Stack direction="column">
              <Typography sx={{ color: "#496682", fontSize: "15px", fontWeight: "400" }}>
                This mailbox has its own email address.
              </Typography>
              <Typography sx={{ color: "#496682", fontSize: "15px", fontWeight: "400" }}>
                Received emails will be displayed here.
              </Typography>
            </Stack>
            <MaterialTooltip title="Copy Deal Email Address" isShow>
              <Button
                onClick={onClickCopy}
                sx={{ color: "#071F35", fontSize: "15px", fontWeight: "400", background: "#f5f6f7" }}
                endIcon={<ContentCopyIcon />}
              >
                {dealEmail}
              </Button>
            </MaterialTooltip>
          </Stack>
        )}
      </Box>

      {email && <EmailView email={email} isOpen={!!email} onClose={() => setEmail(null)} />}
    </Stack>
  );
};

const tableHeadCellSx: TableCellProps["sx"] = {
  bgcolor: "#edf1f9",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};
